import { faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import {
    faPen as faPenSolid,
    faTrash as faTrashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
    IOutputSensorDataConfigSdResponse,
    IPhysicalSensorModelSdResponse,
    SensorStandard,
    TransducerSensitivityFactor,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { NoValue } from 'components/no_value/NoValue';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { usePagedThirdPartySensorConfigsDataSource } from 'custom_hooks/data_hooks/data_source/thirdPartySensorConfigsDataSource';
import { ApprovedText } from 'pages/Settings/ThirdPartySensorModels/ApprovedSensorBadge';
import { EditConfigurationModal } from 'pages/Settings/ThirdPartySensorModels/EditConfigurationModal';
import {
    formatSensorRole,
    getPhysicalMeasurementRangeDisplay,
    getTransducerOutputRangeDisplay,
} from 'pages/Settings/ThirdPartySensorModels/shared';
import { useMemo, useState } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { useThemeValues } from 'styles/theme';
import { excludeNullAndUndefined } from 'utils/array';
import { getId } from 'utils/data';
import { insertSpacesOnCaseChange } from 'utils/string';

type Action = {
    type: 'edit' | 'delete';
    item: IOutputSensorDataConfigSdResponse;
};

export type SensorConfigsTableProps = Readonly<{
    physicalSensorModel: IPhysicalSensorModelSdResponse;
}>;

export function SensorConfigsTable({
    physicalSensorModel: { Id: sensorModelId, SensorStandard: standard },
}: SensorConfigsTableProps) {
    const { secondaryText, link, deleteButton } = useThemeValues();
    const { showToast } = useNotificationContext();
    const [action, setAction] = useState<Action>();

    const dataSource = usePagedThirdPartySensorConfigsDataSource({
        defaultFilter: { sensorModelId },
    });

    const { reloadPage } = dataSource;

    const { mutate: deleteSensorConfig, isPending: isDeleting } = useMutation({
        mutationFn: (configId: string) =>
            sdClient.deleteOutputSensorDataConfig({
                configId,
            }),

        onSuccess: () => {
            reloadPage();
            showToast({
                content: 'Successfully deleted sensor configuration.',
                type: 'success',
            });
        },

        onError: () => {
            showToast({
                content: 'Failed to delete sensor configuration.',
                type: 'error',
            });
        },

        onSettled: () => {
            setAction(undefined);
        },
    });

    const onClose = (updated?: boolean) => {
        if (updated) {
            reloadPage();
        }
        setAction(undefined);
    };

    const columns = useMemo<TableColumn<IOutputSensorDataConfigSdResponse>[]>(
        () =>
            excludeNullAndUndefined([
                {
                    header: () => 'Display Name',
                    id: 'display-name',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            <ApprovedText
                                text={rowData.Name}
                                approved={rowData.Corporation === undefined}
                            />{' '}
                            <DebugValue description="Configuration" value={rowData} />
                        </div>
                    ),
                    primary: true,
                    locked: true,
                },
                {
                    header: () => 'Output Data Stream Common Type',
                    id: 'output-data-stream-common-type',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {insertSpacesOnCaseChange(rowData.OutputSensorCommonType)}
                        </div>
                    ),
                },
                {
                    header: () => 'Output Data Stream Role Code',
                    id: 'output-data-stream-role-code',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {formatSensorRole(rowData.OutputSensorRoleCode)}
                        </div>
                    ),
                },
                {
                    header: () => 'Data Type',
                    id: 'data-type',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">{rowData.DataType.Name}</div>
                    ),
                },
                {
                    header: () => 'Units',
                    id: 'units',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">{rowData.Unit.Name}</div>
                    ),
                },
                standard === SensorStandard.Analog
                    ? {
                          header: () => 'Physical Measurement Range',
                          id: 'physical-measurement-range',
                          value: ({ rowData }) => (
                              <div className="text-nowrap">
                                  {getPhysicalMeasurementRangeDisplay(rowData)}
                              </div>
                          ),
                      }
                    : undefined,
                standard === SensorStandard.Analog
                    ? {
                          header: () => 'Transducer Output Range',
                          id: 'transducer-output-range',
                          value: ({ rowData }) => (
                              <div className="text-nowrap">
                                  {getTransducerOutputRangeDisplay(rowData)}
                              </div>
                          ),
                      }
                    : undefined,
                standard === SensorStandard.Iepe
                    ? {
                          header: () => 'Transducer Sensitivity',
                          id: 'transducer-sensitivity',
                          value: ({ rowData }) => (
                              <div className="text-nowrap">
                                  {rowData.TransducerSensitivity instanceof
                                  TransducerSensitivityFactor
                                      ? rowData.TransducerSensitivity.Sensitivity
                                      : ''}
                                  {` (${rowData.TransducerOutputUnit.Abbreviation}/${rowData.Unit.Abbreviation})`}
                              </div>
                          ),
                      }
                    : undefined,
                {
                    header: () => 'Corporation',
                    id: 'corporation',
                    value: ({ rowData }) => (
                        <div>{rowData.Corporation?.CorporationName ?? <NoValue />}</div>
                    ),
                },
                {
                    header: () => 'Notes',
                    id: 'notes',
                    value: ({ rowData }) => (
                        <div
                            className="word-wrap-anywhere ellipsis-2-lines"
                            title={rowData.Notes}
                        >
                            {rowData.Notes}
                        </div>
                    ),
                },
                {
                    header: () => 'Actions',
                    id: 'actions',
                    locked: true,
                    value: ({ rowData }) => (
                        <ActionMenuTableCell>
                            <HoverIconButton
                                title="Edit Sensor Configuration"
                                icon={faPen}
                                hoverIcon={faPenSolid}
                                color={secondaryText}
                                hoverColor={link}
                                size="14"
                                onClick={() => setAction({ type: 'edit', item: rowData })}
                            />
                            <HoverIconButton
                                title="Delete Sensor Configuration"
                                icon={faTrash}
                                hoverIcon={faTrashSolid}
                                color={secondaryText}
                                hoverColor={deleteButton}
                                onClick={() =>
                                    setAction({ type: 'delete', item: rowData })
                                }
                                disabled={isDeleting}
                            />
                        </ActionMenuTableCell>
                    ),
                },
            ]),
        [deleteButton, isDeleting, link, secondaryText, standard]
    );

    return (
        <div className="px-30">
            <DataSourceTable
                dataSource={dataSource}
                rowKey={getId}
                columns={columns}
                noResultMessage="No sensor configurations found."
            />
            {action?.type === 'delete' && (
                <ConfirmModal
                    title="Delete Sensor Configuration"
                    actionText="delete this third party sensor configuration?"
                    checkText="delete this third party sensor configuration?"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={() => deleteSensorConfig(action.item.Id)}
                    onClose={() => setAction(undefined)}
                />
            )}
            {action?.type === 'edit' && (
                <EditConfigurationModal configuration={action.item} onClose={onClose} />
            )}
        </div>
    );
}
